// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { useAppSelector } from "../store/hooks";
import { selectElectionById } from "../store/elections/electionsSlice";
import { selectElectionEventById } from "../store/electionEvents/electionEventsSlice";
import { useTranslation } from "react-i18next";
import { ROOT_CLASS_PREFIX, toValidClassName, translateFromPresentation } from "@sequentech/ui-core";
var resolveElectionName = function (election, language, eventDefaultLanguageCode) {
    var _a, _b;
    var presentation = election.presentation;
    var defaultLanguageCode = (_b = (_a = presentation === null || presentation === void 0 ? void 0 : presentation.language_conf) === null || _a === void 0 ? void 0 : _a.default_language_code) !== null && _b !== void 0 ? _b : eventDefaultLanguageCode;
    return (translateFromPresentation(presentation, "alias", language) ||
        translateFromPresentation(presentation, "name", language) ||
        (defaultLanguageCode
            ? translateFromPresentation(presentation, "alias", defaultLanguageCode) ||
                translateFromPresentation(presentation, "name", defaultLanguageCode)
            : undefined) ||
        election.alias ||
        election.name ||
        election.id);
};
/**
 * Manages election class on <html> and provides an election class formatter.
 */
export var useElectionClassName = function () {
    var _a, _b;
    var i18n = useTranslation().i18n;
    var _c = useParams(), electionId = _c.electionId, eventId = _c.eventId;
    var election = useAppSelector(selectElectionById(String(electionId !== null && electionId !== void 0 ? electionId : "")));
    var electionEvent = useAppSelector(selectElectionEventById(eventId));
    var language = i18n.resolvedLanguage || i18n.language;
    var eventDefaultLanguageCode = (_b = (_a = electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation) === null || _a === void 0 ? void 0 : _a.language_conf) === null || _b === void 0 ? void 0 : _b.default_language_code;
    var getElectionClassName = useCallback(function (e) {
        return toValidClassName(resolveElectionName(e, language, eventDefaultLanguageCode));
    }, [eventDefaultLanguageCode, language]);
    var activeElectionClassName = useMemo(function () {
        if (!electionId || !election)
            return null;
        return getElectionClassName(election);
    }, [electionId, election, getElectionClassName]);
    useEffect(function () {
        var appRoot = document.querySelector(".app-root");
        if (!appRoot)
            return;
        // Remove any previous e-* class we added
        for (var _i = 0, _a = Array.from(appRoot.classList); _i < _a.length; _i++) {
            var cls = _a[_i];
            if (cls.startsWith(ROOT_CLASS_PREFIX)) {
                appRoot.classList.remove(cls);
            }
        }
        if (activeElectionClassName) {
            appRoot.classList.add(activeElectionClassName);
        }
    }, [activeElectionClassName]);
    return [getElectionClassName, activeElectionClassName];
};
